import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { CustomFieldData, FieldType } from '@klickdata/core/custom-field';
import { Customer } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { SideNaveActionsTypes, SideNaveData } from '@klickdata/core/mobile';
import { SideNaveResponseData } from '@klickdata/core/mobile/src/mobile.service';
import { AppScope } from '@klickdata/core/resource';
import { Utils } from '@klickdata/core/util';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { filter, Observable, Subscription, takeUntil } from 'rxjs';

@Component({
    selector: 'app-menu-side-form-builder',
    templateUrl: './menu-side-form-builder.component.html',
    styleUrls: ['./menu-side-form-builder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSideFormBuilderComponent extends OnDestroyHandler implements OnInit {
    @Input() public navData: SideNaveData;
    @Output() onClose: EventEmitter<SideNaveResponseData> = new EventEmitter();
    SideNaveActionsTypes = SideNaveActionsTypes;
    FieldType = FieldType;
    Utils: Utils;
    public scopes: { value: AppScope; title: Observable<string>; icon: string; isMainResource?: boolean }[];
    public form: FormGroup;
    public checboxInitialValue: FormGroup;
    private checkboxValueSub?: Subscription;
    public customer$: Observable<Customer>;
    public predefinedFieldIdControl: FormControl = new FormControl(null);
    constructor(
        protected fb: FormBuilder,
        protected auth: AuthService,
        protected cdr: ChangeDetectorRef,
        @Inject(LOCALE_ID) protected localeId: string
    ) {
        super();
        this.form = this.fb.group({
            name: [null, Validators.required],
            placeholder: [null],
            initial_value: [''],
            scope_id: [AppScope.USERS],
            order: [1],
            type: [FieldType.TEXT_INPUT, Validators.required],
            disabled: [true],
            options: this.fb.array([this.buildOptionFormGroup()]),
            key: [null],
            editable: [null],
        });
        this.customer$ = this.auth.getCustomer();
        this.localeId = this.localeId === 'en' ? 'en-US' : localeId;
    }
    ngOnInit(): void {
        if (this.navData?.data?.field) {
            this.patchForm(this.navData?.data?.field);
        }
        // Build checkbox group if initial type is MULTIPLE_CHOICE
        if (this.form.get('type')?.value === FieldType.MULTIPLE_CHOICE) {
            this.buildCheckboxInitialValueGroup();
        }

        // Rebuild checkbox group on type change to MULTIPLE_CHOICE
        this.form
            .get('type')
            .valueChanges.pipe(takeUntil(this.destroy))
            .subscribe((type) => {
                this.form.get('initial_value')?.reset();
                if (type === FieldType.MULTIPLE_CHOICE) {
                    this.buildCheckboxInitialValueGroup();
                }
            });
        this.scopes = AppScope.getCustomFieldsScopesOptions().map((scope) => ({
            value: scope.value,
            title: AppScope.getScopeLabel(this.auth.getCustomer(), scope.value, true),
            icon: scope.icon,
            isMainResource: scope.isMainResource,
        }));
        this.predefinedFieldIdControl.valueChanges.pipe(takeUntil(this.destroy)).subscribe((field) => {
            if (field) {
                this.patchForm(field);
            } else {
                this.form.reset();
            }
        });
    }
    private patchForm(field: CustomFieldData) {
        field.disabled = !field.disabled;
        this.form.patchValue(field);
        this.patchOptions(field);
        this.patchInitialValue(field);
    }
    private patchInitialValue(field: CustomFieldData) {
        switch (this.form.value.type) {
            case FieldType.MULTIPLE_CHOICE:
                // For MULTIPLE_CHOICE, initial_value should be an array
                // If backend sends boolean/null, convert to empty array
                // The checkbox group will be built after options are patched
                const multiChoiceValue = Array.isArray(field?.initial_value) ? field.initial_value : [];
                this.form.get('initial_value')?.setValue(multiChoiceValue);
                return;
            case FieldType.SINGLE_CHOICE:
            case FieldType.DROPDOWN:
                this.form.get('initial_value')?.setValue(field?.initial_value);
                return;
            default:
                this.form.get('initial_value')?.setValue(field?.initial_value);
                return;
        }
    }
    private patchOptions(field: CustomFieldData) {
        if (!!field.options?.length) {
            this.optionsFormArray.controls.length = 0;
            field.options.forEach((option) => this.optionsFormArray.push(this.buildOptionFormGroup(option)));
            // If we are on MULTIPLE_CHOICE, rebuild the checkbox group to reflect options
            if (this.form.get('type')?.value === FieldType.MULTIPLE_CHOICE) {
                this.buildCheckboxInitialValueGroup();
            }
        }
    }
    get optionsFormArray(): FormArray {
        return <FormArray>this.form.get('options');
    }

    get formBuilderItemTypes(): FieldType[] {
        return [
            FieldType.TEXT_INPUT,
            FieldType.DATE,
            FieldType.DROPDOWN,
            FieldType.MULTIPLE_CHOICE,
            FieldType.SINGLE_CHOICE,
            FieldType.SWITCH,
        ];
    }
    removeOption(index: number) {
        this.optionsFormArray.removeAt(index);
        FormHelper.markForm(this.form);
    }

    addNewOption() {
        this.optionsFormArray.push(this.buildOptionFormGroup());
        this.form.markAsDirty();
        this.cdr.markForCheck();
        if (this.form.get('type')?.value === FieldType.MULTIPLE_CHOICE) {
            this.buildCheckboxInitialValueGroup();
        }
    }
    private buildOptionFormGroup(value?: { id: number; value: string }): FormGroup {
        return this.fb.group({
            id: [value?.id],
            value: [value?.value],
        });
    }
    public gettranslateValue(value: any) {
        return Utils.getValueFromTranslation(value, this.localeId);
    }

    onSubmit() {
        const data = this.form.value;
        if (!data.options.length || [FieldType.TEXT_INPUT, FieldType.DATE, FieldType.SWITCH].includes(data.type)) {
            delete data.options;
        } else {
            data.options = data.options.filter((option: { id: string; value: string }) => !!option?.value);
        }
        data.initial_value = this.mapInitialValue();
        data.disabled = !this.form.value.disabled;
        this.onClose.emit({
            action: SideNaveActionsTypes.POSITIVE,
            data: this.form.value,
        });
        this.form.reset();
    }

    private mapInitialValue(): any {
        switch (this.form.value.type) {
            case FieldType.MULTIPLE_CHOICE:
                return this.mapMultipleChoiceInitialValue();
            case FieldType.SINGLE_CHOICE:
            case FieldType.DROPDOWN:
                return this.mapSingleChoiceInitialValue();
            default:
                return this.form.value.initial_value;
        }
    }
    private mapMultipleChoiceInitialValue(): any {
        const checkBoxInitials = [];
        Object.entries(this.checboxInitialValue.value).forEach(([key, value]) => {
            checkBoxInitials.push({
                id: Number(key) + 1,
                value: value,
            });
        });
        return checkBoxInitials;
    }
    private mapSingleChoiceInitialValue(): any {
        const selectedOption = this.optionsFormArray.controls.find(
            (option) => option.value === this.form.value.initial_value
        );
        if (!selectedOption) {
            return {};
        }
        return {
            id: selectedOption.value.id ?? this.optionsFormArray.controls.indexOf(selectedOption) + 1,
            value: this.form.value.initial_value,
        };
    }

    private buildCheckboxInitialValueGroup(): void {
        type OptionSelection = { id: number | string; value: boolean };

        // Prepare initial values
        const rawInitial = this.form.get('initial_value')?.value;
        const initialArray: OptionSelection[] = Array.isArray(rawInitial) ? (rawInitial as OptionSelection[]) : [];

        // Build group config keyed by option ID (to match template expectations)
        const groupConfig: Record<string, boolean> = {};
        this.optionsFormArray.controls.forEach((ctrl, index) => {
            const optionId = this.resolveOptionId(ctrl.value, index);
            const key = String(optionId);
            groupConfig[key] = this.isOptionInitiallyChecked(ctrl.value, index, initialArray);
        });

        // Create form group
        this.checboxInitialValue = this.fb.group(groupConfig);

        // Manage previous subscription when rebuilding
        if (this.checkboxValueSub) {
            this.checkboxValueSub.unsubscribe();
            this.checkboxValueSub = undefined;
        }

        // Sync changes back to parent
        this.checkboxValueSub = this.checboxInitialValue.valueChanges
            .pipe(takeUntil(this.destroy))
            .subscribe((valueMap: Record<string, boolean>) => {
                const payload = this.toSelectionPayload(valueMap);
                this.form.get('initial_value')?.setValue(payload, { emitEvent: false });
            });

        // Trigger initial sync once
        const initialPayload = this.toSelectionPayload(this.checboxInitialValue.value as Record<string, boolean>);
        this.form.get('initial_value')?.setValue(initialPayload, { emitEvent: false });
        this.cdr.markForCheck();
    }

    private resolveOptionId(optionValue: any, index: number): number | string {
        const possibleId = optionValue?.id ?? optionValue?.value?.id;
        return (possibleId ?? index) as number | string;
    }

    private isOptionInitiallyChecked(
        optionValue: any,
        index: number,
        initialArray: Array<{ id: number | string; value: boolean }>
    ): boolean {
        if (!initialArray?.length) return false;
        const optionId = this.resolveOptionId(optionValue, index);
        const matched = initialArray.find((x) => String(x?.id) === String(optionId));
        return !!matched?.value;
    }

    private toSelectionPayload(valueMap: Record<string, boolean>): Array<{ id: number | string; value: boolean }> {
        return Object.entries(valueMap).map(([key, checked]) => {
            // Find the option by ID (key is now the option ID, not index)
            const optionControl = this.optionsFormArray.controls.find((ctrl, idx) => {
                const optionId = this.resolveOptionId(ctrl.value, idx);
                return String(optionId) === String(key);
            });

            if (!optionControl) {
                // Fallback: try to parse as index if ID lookup fails
                const idx = Number(key);
                const optionValue = this.optionsFormArray.at(idx)?.value;
                return {
                    id: this.resolveOptionId(optionValue, idx),
                    value: !!checked,
                };
            }

            const idx = this.optionsFormArray.controls.indexOf(optionControl);
            return {
                id: this.resolveOptionId(optionControl.value, idx),
                value: !!checked,
            };
        });
    }
}
